import FormValidationController from "./form_validation_controller"

// Enhanced progress update form with unified validation and progress-specific logic
export default class extends FormValidationController {
  static targets = [
    ...FormValidationController.targets,
    "progress",
    "status",
    "comment",
    "quickSetButton"
  ]

  static values = {
    ...FormValidationController.values,
    requireCommentOnChange: { type: Boolean, default: true },
    originalProgress: { type: Number, default: 0 },
    originalStatus: { type: String, default: "" }
  }

  connect() {
    super.connect()

    // Store original values for change detection
    this.originalProgressValue = this.hasProgressTarget ?
      parseInt(this.progressTarget.value) : 0
    this.originalStatusValue = this.hasStatusTarget ?
      this.statusTarget.value : ""

    // Add progress-specific validation
    if (this.hasProgressTarget) {
      this.progressTarget.addEventListener("input", this.validateProgress.bind(this))
    }

    if (this.hasStatusTarget) {
      this.statusTarget.addEventListener("change", this.validateForm.bind(this))
    }

    // Setup Trix editor for comments
    this.setupTrixEditor()

    // Initial validation
    this.validateForm()
  }

  disconnect() {
    super.disconnect()
    this.cleanupTrixEditor()
  }

  // Override form validation to include progress-update-specific rules
  validateForm() {
    let isValid = super.validateForm()

    // Progress validation
    if (this.hasProgressTarget) {
      if (!this.validateProgressField()) {
        isValid = false
      }
    }

    // Comment requirement validation
    if (this.requireCommentOnChangeValue) {
      if (!this.validateCommentRequirement()) {
        isValid = false
      }
    }

    // Update submit button state
    this.updateSubmitButtonState(isValid)

    return isValid
  }

  validateProgressField() {
    const progress = parseInt(this.progressTarget.value)

    if (isNaN(progress) || progress < 0 || progress > 100) {
      this.showFieldError(this.progressTarget, 'Progress must be between 0 and 100')
      return false
    }

    this.clearFieldError(this.progressTarget)
    return true
  }

  validateCommentRequirement() {
    const progressChanged = this.hasProgressChanged()
    const statusChanged = this.hasStatusChanged()
    const hasComment = this.hasValidComment()

    if ((progressChanged || statusChanged) && !hasComment) {
      const message = 'Please explain your progress update'
      if (this.hasCommentTarget) {
        this.showFieldError(this.commentTarget, message)
      } else {
        this.showFormError(message)
      }
      return false
    }

    if (this.hasCommentTarget) {
      this.clearFieldError(this.commentTarget)
    }
    return true
  }

  // Progress change detection
  hasProgressChanged() {
    if (!this.hasProgressTarget) return false
    return parseInt(this.progressTarget.value) !== this.originalProgressValue
  }

  hasStatusChanged() {
    if (!this.hasStatusTarget) return false
    return this.statusTarget.value !== this.originalStatusValue
  }

  hasValidComment() {
    if (!this.hasCommentTarget) return true

    const content = this.getCommentContent()
    return content && content.trim() !== ""
  }

  getCommentContent() {
    if (!this.hasCommentTarget) return ""

    // Handle both regular textarea and Trix editor
    if (this.commentTarget.tagName === 'TRIX-EDITOR') {
      return this.commentTarget.value
    }
    return this.commentTarget.value
  }

  // Quick progress setters
  quickSet(event) {
    event.preventDefault()
    const value = event.currentTarget.dataset.value

    if (this.hasProgressTarget) {
      this.progressTarget.value = value
      this.validateForm()

      // Visual feedback for quick set
      this.highlightQuickSetButton(event.currentTarget)
    }
  }

  highlightQuickSetButton(button) {
    // Remove active state from all quick set buttons
    this.quickSetButtonTargets.forEach(btn => {
      btn.classList.remove('btn-active')
    })

    // Add active state to selected button
    button.classList.add('btn-active')

    // Focus on comment field if change requires explanation
    if (this.hasProgressChanged() && this.hasCommentTarget) {
      this.commentTarget.focus()
    }
  }

  // Trix editor setup and management
  setupTrixEditor() {
    const trixEditor = this.element.querySelector("trix-editor")
    if (trixEditor) {
      // Remove required attribute to prevent Safari issues
      trixEditor.removeAttribute("required")

      // Store bound reference for proper cleanup
      this._boundHandleTrixChange = this.handleTrixChange.bind(this)

      // Add change listener for validation
      trixEditor.addEventListener("trix-change", this._boundHandleTrixChange)

      // Remove required from hidden input as well
      const hiddenInput = this.element.querySelector('input[type="hidden"][name*="[content]"]')
      if (hiddenInput) {
        hiddenInput.removeAttribute("required")
      }
    }
  }

  cleanupTrixEditor() {
    const trixEditor = this.element.querySelector("trix-editor")
    if (trixEditor && this._boundHandleTrixChange) {
      trixEditor.removeEventListener("trix-change", this._boundHandleTrixChange)
    }
  }

  handleTrixChange(event) {
    const trixEditor = event.target
    const content = trixEditor.editor.getDocument().toString().trim()
    const hiddenInput = this.element.querySelector('input[type="hidden"][name*="[content]"]')

    // Update hidden input with current content
    if (hiddenInput) {
      hiddenInput.value = trixEditor.value
    }

    // Clear errors if content is valid
    if (content !== "") {
      trixEditor.setAttribute("aria-invalid", "false")
      this.clearFieldError(trixEditor)
    }

    // Re-validate form
    this.validateForm()
  }

  // Enhanced success handling for progress updates
  handleSuccessfulSubmission(event) {
    super.handleSuccessfulSubmission(event)

    // Update original values for next change detection
    this.originalProgressValue = this.hasProgressTarget ?
      parseInt(this.progressTarget.value) : 0
    this.originalStatusValue = this.hasStatusTarget ?
      this.statusTarget.value : ""

    // Clear Trix editor content
    const trixEditor = this.element.querySelector("trix-editor")
    if (trixEditor) {
      trixEditor.value = ""
    }

    // Reset quick set buttons
    this.quickSetButtonTargets.forEach(btn => {
      btn.classList.remove('btn-active')
    })

    // Close accordion/details if present
    const details = this.element.closest('details')
    if (details) {
      details.removeAttribute('open')
    }

    // Show success feedback
    this.showSuccessMessage('Progress updated successfully')
  }

  // Update submit button state based on validation
  updateSubmitButtonState(isValid) {
    this.submitTargets.forEach(button => {
      button.disabled = !isValid

      if (isValid) {
        button.classList.remove('btn-disabled')
        button.classList.add('btn-primary')
      } else {
        button.classList.add('btn-disabled')
        button.classList.remove('btn-primary')
      }
    })
  }

  // Override loading text for progress-update-specific messaging
  showLoadingState() {
    this.submitTargets.forEach(button => {
      if (!button.dataset.loadingText) {
        button.dataset.loadingText = 'Saving progress...'
      }
    })

    super.showLoadingState()
  }

  // Custom validation for progress specific logic
  runCustomValidation(field, validationType) {
    switch (validationType) {
      case 'progress-update-progress':
        return this.validateProgressField()
      case 'progress-update-comment':
        return this.validateCommentRequirement()
      default:
        return super.runCustomValidation(field, validationType)
    }
  }
}